import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type AdminUsage,
  type AdminUser,
  addUserLogin,
  createUser,
  getAdminUsage,
  getUserLogins,
  getUsers,
  REJECT_LABELS,
  removeUserLogin,
  setUserStatus,
  switchAccount,
  updateUser,
} from '../lib/api.js';

const STATUS_COLOR: Record<string, string> = {
  active: 'teal',
  invited: 'orange',
  suspended: 'red',
};
const STATUS_LABEL: Record<string, string> = {
  active: 'aktiv',
  invited: 'eingeladen',
  suspended: 'gesperrt',
};

/** Betreiber-Nutzerverwaltung: eingeladene Konten freischalten, sperren, entsperren. */
export function AdminUsers(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-users'],
    queryFn: getUsers,
    refetchInterval: 30_000,
  });

  const [newName, setNewName] = useState('');
  const [manageUser, setManageUser] = useState<AdminUser | null>(null);

  const statusMut = useMutation({
    mutationFn: ({ id, status }: { id: number; status: AdminUser['status'] }) =>
      setUserStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      notifications.show({ message: 'Status geändert', color: 'green' });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  // Account-Switch (Impersonation): danach Vollreload, damit die gesamte App
  // im Kontext des gewählten Kontos neu lädt.
  const switchMut = useMutation({
    mutationFn: (v: { id: number; loginEmail?: string }) => switchAccount(v.id, v.loginEmail),
    onSuccess: () => {
      qc.clear();
      window.location.href = '/';
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  /** Kurzname aus einer Login-Mail (Local-Part, Punkte/Zahlen weg) fürs „Ansehen als"-Label. */
  const shortName = (email: string): string => {
    const local = email.split('@')[0] ?? email;
    const base = local.replace(/[._-]?\d+.*$/, '').replace(/[._-]+/g, ' ').trim();
    return (base || local).replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const createMut = useMutation({
    mutationFn: (name: string) => createUser(name),
    onSuccess: () => {
      setNewName('');
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      notifications.show({ message: 'Konto angelegt', color: 'green' });
    },
    onError: (e) => notifications.show({ message: String(e), color: 'red' }),
  });

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Anchor component={Link} to="/health" size="sm" c="dimmed">
          ← Übersicht
        </Anchor>
        <Title order={1}>Nutzerverwaltung</Title>
        <Text c="dimmed" size="sm">
          Eingeladene Konten freischalten, aktive sperren, in ein Konto wechseln. Nur für den
          Betreiber sichtbar.
        </Text>
      </Stack>

      <Card withBorder padding="md">
        <Stack gap={8}>
          <Text fw={600} size="sm">
            Verwaltetes Konto anlegen
          </Text>
          <Text size="xs" c="dimmed">
            Für Konten, die du selbst betreust (ohne eigenen Google-Login). Anker, Postfach und
            Kriterien richtest du danach per „Ansehen als" ein.
          </Text>
          <Group gap="xs" wrap="nowrap" align="flex-end">
            <TextInput
              placeholder="Name, z. B. Jacqui & Lukas"
              value={newName}
              onChange={(e) => setNewName(e.currentTarget.value)}
              style={{ flex: 1 }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newName.trim()) createMut.mutate(newName.trim());
              }}
            />
            <Button
              onClick={() => createMut.mutate(newName.trim())}
              disabled={!newName.trim() || createMut.isPending}
              loading={createMut.isPending}
            >
              Anlegen
            </Button>
          </Group>
        </Stack>
      </Card>

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}
      {error && (
        <Card>
          <Text c="red.7">Fehler: {String(error)}</Text>
        </Card>
      )}

      {data && (
        <Card padding={0} style={{ overflowX: 'auto' }}>
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Konto</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Objekte</Table.Th>
                <Table.Th>Letzter Login</Table.Th>
                <Table.Th>Aktion</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.users.map((u) => (
                <Table.Tr key={u.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Group gap={6}>
                        <Text fw={600} size="sm">
                          {u.name ?? u.email}
                        </Text>
                        {u.isOwner && (
                          <Badge size="xs" color="violet" variant="light" tt="none">
                            Betreiber
                          </Badge>
                        )}
                      </Group>
                      {u.name && (
                        <Text size="xs" c="dimmed">
                          {u.email}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[u.status]} variant="light" tt="none">
                      {STATUS_LABEL[u.status]}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {u.objectCount}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString('de-AT') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {u.isOwner ? (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    ) : (
                      <Group gap={4} wrap="nowrap">
                        {u.status !== 'active' && (
                          <Button
                            size="xs"
                            variant="light"
                            color="teal"
                            onClick={() => statusMut.mutate({ id: u.id, status: 'active' })}
                            disabled={statusMut.isPending}
                          >
                            Freischalten
                          </Button>
                        )}
                        {u.status === 'active' && (
                          <>
                            {u.logins.length >= 2 ? (
                              // Geteiltes Konto → als konkrete Person ansehen (blinde Queue).
                              u.logins.map((email) => (
                                <Button
                                  key={email}
                                  size="xs"
                                  variant="filled"
                                  color="indigo"
                                  onClick={() => switchMut.mutate({ id: u.id, loginEmail: email })}
                                  disabled={switchMut.isPending}
                                  title={email}
                                >
                                  👁 als {shortName(email)}
                                </Button>
                              ))
                            ) : (
                              <Button
                                size="xs"
                                variant="filled"
                                color="indigo"
                                onClick={() => switchMut.mutate({ id: u.id })}
                                disabled={switchMut.isPending}
                              >
                                Ansehen als →
                              </Button>
                            )}
                            <Button
                              size="xs"
                              variant="light"
                              color="gray"
                              onClick={() => setManageUser(u)}
                            >
                              Verwalten
                            </Button>
                            <Button
                              size="xs"
                              variant="light"
                              color="red"
                              onClick={() => statusMut.mutate({ id: u.id, status: 'suspended' })}
                              disabled={statusMut.isPending}
                            >
                              Sperren
                            </Button>
                          </>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <UsageSection />

      <ManageModal user={manageUser} onClose={() => setManageUser(null)} />
    </Stack>
  );
}

/** Nutzungs-Übersicht: pro Konto Swipe-/Kontakt-Zahlen, Positiv-Quote, Ablehngründe. */
function UsageSection(): JSX.Element {
  const { data } = useQuery({ queryKey: ['admin-usage'], queryFn: getAdminUsage, refetchInterval: 60_000 });
  const rows = (data?.usage ?? []).filter((u) => u.objects > 0);
  return (
    <Stack gap="sm">
      <Title order={3}>Nutzung</Title>
      {rows.length === 0 && (
        <Card>
          <Text c="dimmed" size="sm">
            Noch keine Aktivität.
          </Text>
        </Card>
      )}
      {rows.map((u) => (
        <UsageCard key={u.id} u={u} />
      ))}
    </Stack>
  );
}

function UsageCard({ u }: { u: AdminUsage }): JSX.Element {
  const pct = u.positiveRate !== null ? Math.round(u.positiveRate * 100) : null;
  const maxReason = Math.max(1, ...u.rejectReasons.map((r) => r.count));
  return (
    <Card withBorder padding="md">
      <Stack gap="sm">
        <Group justify="space-between" wrap="wrap" gap="xs">
          <Text fw={700}>{u.name ?? u.email}</Text>
          <Text size="xs" c="dimmed">
            {u.lastActivityAt
              ? `zuletzt aktiv ${new Date(u.lastActivityAt).toLocaleDateString('de-AT')}`
              : 'keine Swipes'}
          </Text>
        </Group>

        <Group gap="lg" wrap="wrap">
          <Metric label="Objekte" value={u.objects} />
          <Metric label="Geswiped" value={u.swiped} />
          <Metric label="★ Hot" value={u.hot} color="pink.7" />
          <Metric label="☎ Kontaktiert" value={u.contacted} color="cyan.7" />
          <Metric label="✗ Abgelehnt" value={u.rejected} color="red.7" />
          <Metric label="Positiv-Quote" value={pct !== null ? `${pct}%` : '—'} color="teal.7" />
        </Group>

        {u.rejectReasons.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.05em' }}>
              Ablehngründe
            </Text>
            {u.rejectReasons.map((r) => (
              <Group key={r.category} gap="sm" wrap="nowrap" align="center">
                <Text size="sm" style={{ width: 150, flexShrink: 0 }}>
                  {REJECT_LABELS[r.category] ?? r.category}
                </Text>
                <div
                  style={{
                    height: 10,
                    borderRadius: 5,
                    background: '#c4b5fd',
                    width: `${Math.round((r.count / maxReason) * 100)}%`,
                    minWidth: 6,
                  }}
                />
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {r.count}
                </Text>
              </Group>
            ))}
          </Stack>
        )}

        {u.logins.length > 0 && (
          <Stack gap={4}>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.05em' }}>
              Aktivität nach Login
            </Text>
            {u.logins.map((l) => (
              <Group key={l.login} gap="sm" wrap="wrap" align="center" justify="space-between">
                <Text size="sm" style={{ fontWeight: 500, wordBreak: 'break-all' }}>
                  {l.login}
                </Text>
                <Group gap="md" wrap="nowrap">
                  <Text size="sm" c="pink.7" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    ★ {l.positiv}
                  </Text>
                  <Text size="sm" c="red.7" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    ✗ {l.abgelehnt}
                  </Text>
                  {l.sonstige > 0 && (
                    <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      +{l.sonstige}
                    </Text>
                  )}
                </Group>
              </Group>
            ))}
          </Stack>
        )}

        {u.devices.length > 0 && (
          <Stack gap={6}>
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.05em' }}>
              Geräte & Benachrichtigungen
            </Text>
            {u.devices.map((d, i) => (
              <DeviceRow key={`${d.loginEmail ?? '?'}-${d.platform}-${i}`} d={d} />
            ))}
          </Stack>
        )}

        <ActivityHistory u={u} />
      </Stack>
    </Card>
  );
}

function relTimeShort(ms: number | null): string {
  if (!ms) return 'nie';
  const diff = Date.now() - ms;
  const min = Math.round(diff / 60000);
  if (min < 1) return 'gerade eben';
  if (min < 60) return `vor ${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `vor ${h} h`;
  const d = Math.round(h / 24);
  return `vor ${d} d`;
}

/** Echter Verlauf: Swipe-Aktivität pro Tag (30 T, rückwirkend) + Besuche pro Login. */
function ActivityHistory({ u }: { u: AdminUsage }): JSX.Element | null {
  const totalSwipes = u.activityByDay.reduce((s, d) => s + d.swipes, 0);
  const maxDay = Math.max(1, ...u.activityByDay.map((d) => d.swipes));
  if (totalSwipes === 0 && u.visits.length === 0) return null;
  return (
    <Stack gap={6}>
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed" fw={600} tt="uppercase" style={{ letterSpacing: '0.05em' }}>
          Verlauf (30 Tage)
        </Text>
        <Text size="xs" c="dimmed">
          {totalSwipes} Swipes · zuletzt online {relTimeShort(u.lastVisitAt)}
        </Text>
      </Group>

      {/* Balken: Swipes pro Tag (älteste links, heute rechts) */}
      <Group gap={2} align="flex-end" style={{ height: 44 }} wrap="nowrap">
        {u.activityByDay.map((d) => (
          <div
            key={d.day}
            title={`${d.day}: ${d.swipes} Swipes`}
            style={{
              flex: 1,
              minWidth: 0,
              height: `${Math.max(3, Math.round((d.swipes / maxDay) * 44))}px`,
              background: d.swipes > 0 ? '#7c3aed' : '#e2e8f0',
              borderRadius: 2,
            }}
          />
        ))}
      </Group>

      {/* Besuche pro Login */}
      {u.visits.length > 0 && (
        <Stack gap={2}>
          {u.visits.map((v) => (
            <Group key={v.login ?? '?'} justify="space-between" wrap="wrap" gap="xs">
              <Text size="xs" style={{ wordBreak: 'break-all' }}>
                {v.login ?? 'unbekanntes Login'}
              </Text>
              <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                zuletzt {relTimeShort(v.lastVisitAt)} · {v.visits30d} Besuche (30 T)
              </Text>
            </Group>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function Chip({ label, tone }: { label: string; tone: 'ok' | 'off' | 'bad' | 'warn' }): JSX.Element {
  const c =
    tone === 'ok'
      ? { bg: '#dcfce7', fg: '#166534' }
      : tone === 'bad'
        ? { bg: '#fee2e2', fg: '#991b1b' }
        : tone === 'warn'
          ? { bg: '#fef3c7', fg: '#92400e' }
          : { bg: '#f1f5f9', fg: '#64748b' };
  return (
    <Text
      component="span"
      style={{
        background: c.bg,
        color: c.fg,
        fontSize: '0.72rem',
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 999,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Text>
  );
}

function DeviceRow({ d }: { d: AdminUsage['devices'][number] }): JSX.Element {
  // OS-BEWUSSTER Benachrichtigungs-Status — die Eigenheiten pro Betriebssystem:
  //  • iOS/iPadOS: Web-Push geht NUR wenn die PWA am Home-Bildschirm installiert ist
  //    (im Safari-Tab unmöglich). Ein in den iOS-Einstellungen abgedrehter Push kippt
  //    `permission` NICHT auf denied → das können wir clientseitig nicht sehen.
  //  • Android/Desktop: Push geht direkt im Browser-Tab, PWA-Installation optional.
  // Reihenfolge: blockiert > an > (iOS-ohne-PWA = Installation nötig) > kein-Support > aus.
  const notif =
    d.permission === 'denied'
      ? { label: '🔕 blockiert', tone: 'bad' as const }
      : d.subscribed && d.permission === 'granted'
        ? { label: '🔔 an', tone: 'ok' as const }
        : d.ios && !d.standalone
          ? { label: '📲 PWA-Installation nötig', tone: 'warn' as const }
          : !d.supported
            ? { label: '🚫 kein Push möglich', tone: 'off' as const }
            : { label: '🔔 aus', tone: 'off' as const };
  // Installations-Status. Auf iOS ist die Installation Pflicht für Push (deshalb warn,
  // wenn nicht installiert); auf Android/Desktop nur „nice to have" (deshalb neutral).
  const pwa = d.standalone
    ? { label: 'PWA ✓', tone: 'ok' as const }
    : d.ios
      ? { label: 'nicht installiert', tone: 'warn' as const }
      : { label: 'Browser-Tab', tone: 'off' as const };
  return (
    <Group gap="xs" wrap="wrap" align="center" justify="space-between">
      <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
        <Text size="sm" fw={500}>
          {d.platform}
        </Text>
        <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all' }}>
          {d.loginEmail ?? 'unbekanntes Login'}
        </Text>
      </Group>
      <Group gap={6} wrap="nowrap">
        <Chip label={pwa.label} tone={pwa.tone} />
        <Chip label={notif.label} tone={notif.tone} />
      </Group>
    </Group>
  );
}

function Metric({ label, value, color }: { label: string; value: number | string; color?: string }): JSX.Element {
  return (
    <Stack gap={0}>
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} style={{ letterSpacing: '0.04em', fontSize: '0.62rem' }}>
        {label}
      </Text>
      <Text fw={800} size="lg" c={color} style={{ fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </Text>
    </Stack>
  );
}

/** Modal: Konto-(Benachrichtigungs-)E-Mail ändern + erlaubte Google-Login-E-Mails verwalten. */
function ManageModal({ user, onClose }: { user: AdminUser | null; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [newLogin, setNewLogin] = useState('');

  useEffect(() => {
    setEmail(user?.email ?? '');
    setNewLogin('');
  }, [user]);

  const { data: loginsData } = useQuery({
    queryKey: ['user-logins', user?.id],
    queryFn: () => getUserLogins(user!.id),
    enabled: user !== null,
  });

  const done = (msg: string): void => {
    qc.invalidateQueries({ queryKey: ['user-logins', user?.id] });
    qc.invalidateQueries({ queryKey: ['admin-users'] });
    notifications.show({ message: msg, color: 'green' });
  };
  const fail = (e: unknown): void => {
    notifications.show({ message: String(e), color: 'red' });
  };

  const emailMut = useMutation({
    mutationFn: () => updateUser(user!.id, { email: email.trim() }),
    onSuccess: () => done('Benachrichtigungs-E-Mail gespeichert'),
    onError: fail,
  });
  const addMut = useMutation({
    mutationFn: () => addUserLogin(user!.id, newLogin.trim()),
    onSuccess: () => {
      setNewLogin('');
      done('Login-E-Mail hinzugefügt');
    },
    onError: fail,
  });
  const removeMut = useMutation({
    mutationFn: (loginId: number) => removeUserLogin(user!.id, loginId),
    onSuccess: () => done('Login-E-Mail entfernt'),
    onError: fail,
  });

  return (
    <Modal opened={user !== null} onClose={onClose} title={`Verwalten: ${user?.name ?? user?.email ?? ''}`} centered radius="lg">
      <Stack gap="lg">
        <Stack gap={6}>
          <Text size="sm" fw={600}>
            Benachrichtigungs-E-Mail
          </Text>
          <Text size="xs" c="dimmed">
            Dorthin gehen die E-Mail-Benachrichtigungen dieses Kontos.
          </Text>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              style={{ flex: 1 }}
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              placeholder="name@gmail.com"
            />
            <Button onClick={() => emailMut.mutate()} disabled={emailMut.isPending || !email.trim()}>
              Speichern
            </Button>
          </Group>
        </Stack>

        <Divider />

        <Stack gap={6}>
          <Text size="sm" fw={600}>
            Google-Login-E-Mails
          </Text>
          <Text size="xs" c="dimmed">
            Diese Google-Konten dürfen sich in dieses (geteilte) Konto einloggen.
          </Text>
          <Stack gap={6}>
            {(loginsData?.logins ?? []).map((l) => (
              <Group key={l.id} justify="space-between" wrap="nowrap">
                <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
                  <Text size="sm" style={{ wordBreak: 'break-all' }}>
                    {l.email}
                  </Text>
                  {l.linked && (
                    <Badge size="xs" color="teal" variant="light" tt="none">
                      verknüpft
                    </Badge>
                  )}
                </Group>
                <ActionIcon
                  color="red"
                  variant="light"
                  onClick={() => removeMut.mutate(l.id)}
                  disabled={removeMut.isPending}
                  aria-label="Entfernen"
                >
                  ✕
                </ActionIcon>
              </Group>
            ))}
            {(loginsData?.logins ?? []).length === 0 && (
              <Text size="xs" c="dimmed">
                Noch keine Login-E-Mails.
              </Text>
            )}
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <TextInput
              style={{ flex: 1 }}
              value={newLogin}
              onChange={(e) => setNewLogin(e.currentTarget.value)}
              placeholder="neue Google-E-Mail hinzufügen"
            />
            <Button
              variant="light"
              onClick={() => addMut.mutate()}
              disabled={addMut.isPending || !newLogin.trim()}
            >
              + Hinzufügen
            </Button>
          </Group>
        </Stack>
      </Stack>
    </Modal>
  );
}
